import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Search, FileText, Plus, Lock, DollarSign, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import InspectionReviewPanel from "./InspectionReviewPanel";
import InspectionQueueWidget from "./InspectionQueueWidget";
import DuplicateJobsPanel from "./DuplicateJobsPanel";
import HandoffQueueWidget from "./HandoffQueueWidget";
import { ModerationQueueWidget } from "./ModerationQueueWidget";
import { SmsConsentCheckbox } from "@/components/SmsConsentCheckbox";

const statusColors = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  offered: "bg-indigo-50 text-indigo-700 border-indigo-200",
  viewed: "bg-purple-50 text-purple-700 border-purple-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_route: "bg-cyan-50 text-cyan-700 border-cyan-200",
  passed: "bg-amber-50 text-amber-700 border-amber-200",
  missed: "bg-rose-50 text-rose-700 border-rose-200",
  reassigned: "bg-orange-50 text-orange-700 border-orange-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-800 text-white border-zinc-700",
  canceled: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const statusLabels = {
  new: "New", offered: "Offered", viewed: "Viewed", accepted: "Accepted",
  en_route: "En Route", passed: "Passed", missed: "Missed",
  reassigned: "Reassigned", in_progress: "In Progress",
  completed: "Completed", closed: "Closed (Paid)", canceled: "Canceled",
};

export default function RequestMonitoring() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showClose, setShowClose] = useState(null);
  const [closeNotes, setCloseNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState(false);
  // ── Detail panel (Part 1 additive) ──
  const [detailReq, setDetailReq] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [vendors, setVendors] = useState([]);
  const [savingDetail, setSavingDetail] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [vendorDraft, setVendorDraft] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [newReq, setNewReq] = useState({
    service_type_label: "", urgency_level: "medium", problem_description: "",
    customer_name: "", customer_phone: "", property_address_line1: "",
    property_city: "", property_state: "", property_zip_code: ""
  });

  const fetchRequests = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterUrgency !== "all") params.set("urgency", filterUrgency);
      params.set("limit", "50");
      const { data } = await API.get(`/management/requests?${params}`);
      setRequests(data.requests);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [search, filterStatus, filterUrgency]);

  // ── Vendor list for the assignment dropdown (Part 1 additive) ──
  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/management/vendors?limit=200");
        setVendors(data.vendors || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const openDetail = (req, tab = "overview") => {
    setDetailReq(req);
    setDetailTab(tab);
    setStatusDraft(req.current_status || "");
    setVendorDraft(req.assigned_vendor_id || "");
    setReassignReason("");
  };

  // Open detail dialog by request_id — used by the Inspection Queue widget
  // when the job isn't on the currently-filtered page.
  const openDetailById = async (requestId, tab = "overview") => {
    let req = requests.find((r) => r.id === requestId);
    if (!req) {
      try {
        const { data } = await API.get(`/requests/${requestId}`);
        req = data;
      } catch (err) {
        toast.error("Could not load job");
        return;
      }
    }
    openDetail(req, tab);
  };

  const saveDetail = async () => {
    if (!detailReq) return;
    const statusChanged = statusDraft && statusDraft !== detailReq.current_status;
    const vendorChanged = vendorDraft && vendorDraft !== (detailReq.assigned_vendor_id || "");
    if (!statusChanged && !vendorChanged) { setDetailReq(null); return; }
    setSavingDetail(true);
    try {
      if (vendorChanged) {
        await API.put(`/management/requests/${detailReq.id}/reassign`, {
          vendor_id: vendorDraft,
          reason: reassignReason || "Reassigned via job detail panel",
        });
      }
      if (statusChanged) {
        await API.put(`/requests/${detailReq.id}/status`, {
          status: statusDraft,
          notes: "Status updated via job detail panel",
        });
      }
      toast.success("Job updated");
      setDetailReq(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSavingDetail(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await API.post("/management/requests", newReq);
      toast.success("Request created and dispatched");
      setShowCreate(false);
      setNewReq({ service_type_label: "", urgency_level: "medium", problem_description: "",
        customer_name: "", customer_phone: "", property_address_line1: "",
        property_city: "", property_state: "", property_zip_code: "" });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleCloseJob = async () => {
    if (!showClose) return;
    setClosing(true);
    try {
      await API.put(`/requests/${showClose.id}/status`, {
        status: "closed", notes: closeNotes || "Payment received and job closed."
      });
      toast.success("Job closed — marked as paid");
      setShowClose(null);
      setCloseNotes("");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to close job");
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="request-monitoring">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Requests</h1>
          <p className="text-sm text-zinc-500">{total} requests total</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="create-request-button">
          <Plus className="w-4 h-4 mr-2" /> New Request
        </Button>
      </div>

      {/* ARM Handoff Queue + Inspection Queue + Duplicate Jobs widgets (additive) */}
      <HandoffQueueWidget onOpenJob={openDetailById} onAfterChange={fetchRequests} />
      <ModerationQueueWidget />
      <InspectionQueueWidget onOpenJob={openDetailById} refreshKey={requests.length} />
      <DuplicateJobsPanel onAfterDelete={fetchRequests} />

      {/* Filters */}
      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search requests..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9" data-testid="request-search-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-44" data-testid="filter-request-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterUrgency} onValueChange={setFilterUrgency}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-urgency">
                <SelectValue placeholder="Urgency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Urgency</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-zinc-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Request</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Urgency</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Customer</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Technician</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Location</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Created</TableHead>
                <TableHead className="text-xs">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
              ) : requests.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">No requests found</TableCell></TableRow>
              ) : requests.map(req => (
                <TableRow key={req.id} data-testid={`request-row-${req.id}`}
                          onClick={() => openDetail(req)}
                          className="cursor-pointer hover:bg-zinc-50">
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-medium text-zinc-900">{req.service_type_label}</p>
                        {req.handoff_source === "arm" && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-sky-600 text-white"
                            title={`From ARM Dispatch · ${req.handoff_status || "pending_review"}`}
                            data-testid={`arm-badge-${req.id}`}
                          >
                            ARM
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-[10px] text-zinc-400">{req.request_code}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusColors[req.current_status] || ""}`}>
                      {statusLabels[req.current_status] || req.current_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full urgency-${req.urgency_level}`}>
                      {req.urgency_level}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-zinc-600">{req.customer_name || "\u2014"}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-zinc-600">{req.assigned_vendor_name || "Unassigned"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-zinc-500">{req.property_city}, {req.property_state} {req.property_zip_code}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-zinc-500">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {req.current_status === "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setShowClose(req); }}
                        className="text-xs border-zinc-300 hover:bg-zinc-100"
                        data-testid={`close-job-${req.id}`}
                      >
                        <Lock className="w-3 h-3 mr-1" /> Close
                      </Button>
                    )}
                    {req.current_status === "closed" && (
                      <Badge className="bg-zinc-800 text-white text-[10px]">
                        <DollarSign className="w-3 h-3 mr-0.5" /> Paid
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Job Detail Panel (Part 1 additive) */}
      <Dialog open={!!detailReq} onOpenChange={(o) => !o && setDetailReq(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-detail-panel">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Job Details
            </DialogTitle>
          </DialogHeader>
          {detailReq && (
            <Tabs value={detailTab} onValueChange={setDetailTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-2" data-testid="job-detail-tabs">
                <TabsTrigger value="overview" data-testid="job-tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="inspection" data-testid="job-tab-inspection">
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
                  Inspection
                  {detailReq.estimate_status === "Estimate Pending Review" && (
                    <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500" data-testid="inspection-pending-dot" />
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-3 text-sm">
              <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                <p className="font-mono text-[10px] text-zinc-400" data-testid="detail-id">{detailReq.request_code}</p>
                <p className="text-base font-semibold text-zinc-900" data-testid="detail-service">{detailReq.service_type_label}</p>
                {detailReq.problem_description && (
                  <p className="text-[11px] text-zinc-600 mt-1">{detailReq.problem_description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-bold">Customer</p>
                  <p className="text-zinc-900" data-testid="detail-customer">{detailReq.customer_name || "—"}</p>
                  {detailReq.customer_phone && (
                    <p className="text-[11px] text-zinc-500">{detailReq.customer_phone}</p>
                  )}
                  {detailReq.customer_email && (
                    <p className="text-[11px] text-zinc-500">{detailReq.customer_email}</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-bold">Urgency</p>
                  <Badge className="text-[10px] mt-0.5">{detailReq.urgency_level}</Badge>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase text-zinc-500 font-bold">Address</p>
                <p className="text-zinc-800" data-testid="detail-address">
                  {detailReq.property_address_line1 || "—"}
                  {detailReq.property_city && `, ${detailReq.property_city}`}
                  {detailReq.property_state && `, ${detailReq.property_state}`}
                  {detailReq.property_zip_code && ` ${detailReq.property_zip_code}`}
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase text-zinc-500 font-bold">Created</p>
                <p className="text-zinc-800" data-testid="detail-created">
                  {new Date(detailReq.created_at).toLocaleString()}
                </p>
              </div>

              {detailReq.notes && (
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-bold">Notes</p>
                  <p className="text-zinc-800 whitespace-pre-wrap" data-testid="detail-notes">{detailReq.notes}</p>
                </div>
              )}

              <div className="border-t border-zinc-200 pt-3 space-y-3">
                <p className="text-[10px] uppercase text-red-600 font-bold">Editable</p>

                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={statusDraft} onValueChange={setStatusDraft}>
                    <SelectTrigger className="mt-1" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, l]) =>
                        <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Assigned Technician</Label>
                  <Select value={vendorDraft || "unassigned"} onValueChange={v => setVendorDraft(v === "unassigned" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="detail-vendor-select"><SelectValue placeholder="Select technician" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {vendors.map(v =>
                        <SelectItem key={v.id} value={v.id}>{v.full_name || v.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {vendorDraft && vendorDraft !== (detailReq.assigned_vendor_id || "") && (
                    <Input value={reassignReason} onChange={e => setReassignReason(e.target.value)}
                           placeholder="Reassignment reason (optional)"
                           className="mt-2 text-xs"
                           data-testid="detail-reassign-reason" />
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setDetailReq(null)} className="flex-1">Close</Button>
                <Button onClick={saveDetail} disabled={savingDetail}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="detail-save-btn">
                  {savingDetail
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : "Save Changes"}
                </Button>
              </div>
            </div>
              </TabsContent>

              <TabsContent value="inspection">
                <InspectionReviewPanel
                  requestId={detailReq.id}
                  onAfterAction={() => fetchRequests()}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Job Dialog */}
      <Dialog open={!!showClose} onOpenChange={() => { setShowClose(null); setCloseNotes(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Close Job — Mark as Paid</DialogTitle>
          </DialogHeader>
          {showClose && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-lg p-3">
                <p className="font-mono text-[10px] text-zinc-400">{showClose.request_code}</p>
                <p className="text-sm font-medium text-zinc-900">{showClose.service_type_label}</p>
                <p className="text-xs text-zinc-500 mt-1">{showClose.customer_name} &middot; {showClose.property_city}</p>
              </div>
              <div>
                <Label className="text-xs">Payment Notes (optional)</Label>
                <Textarea
                  value={closeNotes}
                  onChange={e => setCloseNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g., Paid via check #1234, invoice #5678..."
                  className="mt-1"
                  data-testid="close-job-notes"
                />
              </div>
              <Button
                onClick={handleCloseJob}
                disabled={closing}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="confirm-close-job"
              >
                {closing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Close Job &amp; Mark Paid</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Request Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create New Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Service Type</Label>
              <Input value={newReq.service_type_label} onChange={e => setNewReq(p => ({ ...p, service_type_label: e.target.value }))}
                placeholder="e.g., Burst Pipe Repair" data-testid="new-req-service" className="mt-1" /></div>
            <div><Label className="text-xs">Urgency</Label>
              <Select value={newReq.urgency_level} onValueChange={v => setNewReq(p => ({ ...p, urgency_level: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-req-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Description</Label>
              <Textarea value={newReq.problem_description} onChange={e => setNewReq(p => ({ ...p, problem_description: e.target.value }))}
                rows={3} data-testid="new-req-description" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Customer Name</Label>
                <Input value={newReq.customer_name} onChange={e => setNewReq(p => ({ ...p, customer_name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Customer Phone</Label>
                <Input value={newReq.customer_phone} onChange={e => setNewReq(p => ({ ...p, customer_phone: e.target.value }))} className="mt-1" data-testid="create-request-phone" /></div>
            </div>
            {newReq.customer_phone && (
              <SmsConsentCheckbox checked={!!newReq.customer_sms_opt_in}
                onChange={v => setNewReq(p => ({ ...p, customer_sms_opt_in: v }))} testId="create-request-sms-opt-in" />
            )}
            <div><Label className="text-xs">Address</Label>
              <Input value={newReq.property_address_line1} onChange={e => setNewReq(p => ({ ...p, property_address_line1: e.target.value }))} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">City</Label>
                <Input value={newReq.property_city} onChange={e => setNewReq(p => ({ ...p, property_city: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">State</Label>
                <Input value={newReq.property_state} onChange={e => setNewReq(p => ({ ...p, property_state: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">ZIP</Label>
                <Input value={newReq.property_zip_code} onChange={e => setNewReq(p => ({ ...p, property_zip_code: e.target.value }))}
                  data-testid="new-req-zip" className="mt-1" /></div>
            </div>
            <Button onClick={handleCreate} disabled={creating || !newReq.service_type_label || !newReq.problem_description}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="submit-new-request">
              {creating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Create & Dispatch"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
